import { useEffect, useRef, useState } from 'react';
import {
  createLoadingAuthGateState,
  getAuthScreenTitle,
  readAuthRedirectError,
  resolveAuthGateState,
  type AuthGateState,
  type AuthRevalidationFn,
  type AuthRevalidationMode,
} from '../lib/auth';
import { startInitialHeatmapTiming } from '../lib/timing';

interface UseAuthGateResult {
  authGateState: AuthGateState;
  authRedirectError: string | null;
  revalidateAuth: AuthRevalidationFn;
  retryAuth: () => void;
}

export function useAuthGate(): UseAuthGateResult {
  const [authGateState, setAuthGateState] = useState<AuthGateState>(createLoadingAuthGateState());
  const [authRedirectError] = useState(() => readAuthRedirectError());
  const authRequestIdRef = useRef(0);

  useEffect(() => {
    startInitialHeatmapTiming();
    let cancelled = false;
    const requestId = ++authRequestIdRef.current;

    void (async () => {
      const nextState = await resolveAuthGateState();
      if (cancelled || requestId !== authRequestIdRef.current) {
        return;
      }

      setAuthGateState(nextState);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (authRedirectError == null || typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('authError');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [authRedirectError]);

  const screenTitle = getAuthScreenTitle(authGateState);
  useEffect(() => {
    if (screenTitle != null) {
      document.title = screenTitle;
    }
  }, [screenTitle]);

  const revalidateAuth: AuthRevalidationFn = async (
    mode: AuthRevalidationMode = 'background',
  ): Promise<AuthGateState> => {
    const requestId = ++authRequestIdRef.current;
    if (mode === 'loading') {
      setAuthGateState(createLoadingAuthGateState());
    }

    const nextState = await resolveAuthGateState();
    if (requestId === authRequestIdRef.current) {
      setAuthGateState(nextState);
    }

    return nextState;
  };

  return {
    authGateState,
    authRedirectError,
    revalidateAuth,
    retryAuth: () => {
      void revalidateAuth('loading');
    },
  };
}
